import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 min

const AdminAuthContext = createContext(null);

/**
 * Admin auth — hybrid httpOnly cookie + in-memory Bearer token.
 * - Primary: httpOnly cookie `hexxium_admin_token` (XSS-proof, set by backend)
 * - Fallback: in-memory Bearer token (survives re-render, lost on full page refresh)
 *
 * On page refresh, the cookie alone is sufficient to re-establish session via /auth/me.
 * No sensitive data is stored in sessionStorage/localStorage.
 */
export function AdminAuthProvider({ children }) {
  const [adminToken, setAdminToken] = useState(null); // in-memory only
  const [admin, setAdmin] = useState(null);
  const [authStep, setAuthStep] = useState("login");
  const [loading, setLoading] = useState(true);
  const activityTimer = useRef(null);

  // Axios helper for admin API calls.
  // Cookies are sent automatically on same-origin requests (production preview/deployed).
  // In-memory Bearer token serves as fallback and works across hybrid auth scenarios.
  const axiosAdmin = useRef(
    axios.create({ baseURL: API })
  ).current;

  const logout = useCallback(async () => {
    try {
      await axiosAdmin.post("/api/admin/auth/logout");
    } catch (_e) {
      // Best-effort; cookie clear may fail but client state is still reset below
    }
    setAdminToken(null);
    setAdmin(null);
    setAuthStep("login");
  }, [axiosAdmin]);

  const resetInactivityTimer = useCallback(() => {
    if (activityTimer.current) clearTimeout(activityTimer.current);
    activityTimer.current = setTimeout(() => {
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [logout]);

  useEffect(() => {
    if (authStep === "complete") {
      const events = ["mousedown", "keydown", "scroll", "touchstart"];
      events.forEach(e => window.addEventListener(e, resetInactivityTimer));
      resetInactivityTimer();
      return () => {
        events.forEach(e => window.removeEventListener(e, resetInactivityTimer));
        if (activityTimer.current) clearTimeout(activityTimer.current);
      };
    }
  }, [authStep, resetInactivityTimer]);

  // On mount: attempt to restore session via httpOnly cookie
  useEffect(() => {
    axiosAdmin.get("/api/admin/auth/me")
      .then(res => {
        setAdmin(res.data);
        if (res.data.must_reset_password) setAuthStep("reset_password");
        else if (res.data.must_setup_mfa && !res.data.mfa_enabled) setAuthStep("setup_mfa");
        else setAuthStep("complete");
      })
      .catch(() => {
        setAdmin(null);
        setAdminToken(null);
        setAuthStep("login");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const res = await axiosAdmin.post("/api/admin/auth/login", { email, password });
    const { token, step, role } = res.data;
    setAdminToken(token); // in-memory fallback (cookie already set by server)
    setAdmin({ email, role, id: res.data.admin_id });
    setAuthStep(step === "complete" ? "complete" : step);
    return res.data;
  };

  const resetPassword = async (newPassword) => {
    const res = await axiosAdmin.post("/api/admin/auth/reset-password",
      { new_password: newPassword }
    );
    setAdminToken(res.data.token);
    if (res.data.step === "complete") {
      const meRes = await axiosAdmin.get("/api/admin/auth/me");
      setAdmin(meRes.data);
    }
    setAuthStep(res.data.step === "complete" ? "complete" : res.data.step);
    return res.data;
  };

  const setupMFA = async () => {
    const res = await axiosAdmin.get("/api/admin/auth/setup-mfa");
    return res.data;
  };

  const confirmMFA = async (code) => {
    const res = await axiosAdmin.post("/api/admin/auth/confirm-mfa", { code });
    setAdminToken(res.data.token);
    const meRes = await axiosAdmin.get("/api/admin/auth/me");
    setAdmin(meRes.data);
    setAuthStep("complete");
    return res.data;
  };

  const verifyMFA = async (code) => {
    const res = await axiosAdmin.post("/api/admin/auth/verify-mfa", { code });
    setAdminToken(res.data.token);
    const meRes = await axiosAdmin.get("/api/admin/auth/me");
    setAdmin(meRes.data);
    setAuthStep("complete");
    return res.data;
  };

  // Returns Authorization header if in-memory token available (fallback for cross-origin or cookie issues).
  // Cookie is sent automatically by browser on same-origin requests.
  // Memoized so consumers can safely list it in hook dependency arrays.
  const getHeaders = useCallback(
    () => (adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
    [adminToken]
  );

  const hasPermission = (permission) => {
    if (!admin) return false;
    if (admin.role === "super_admin") return true;
    const perms = admin.permissions || [];
    return perms.includes("all") || perms.includes(permission);
  };

  return (
    <AdminAuthContext.Provider value={{
      admin, adminToken, authStep, loading,
      login, logout, resetPassword, setupMFA, confirmMFA, verifyMFA,
      getHeaders, hasPermission,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
